import { FC, useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Alert, Autocomplete, Box, FormInputText, InputLabel, Text } from '@hopskipdrive/react-components';
import { RiderLocation } from '@rideiq/riders/features/RiderLocations/Location';
import { Button } from '@rideiq/shared/components/Button';
import { PageHeading } from '@rideiq/shared/components/PageHeading';

import { ContactGroupFormV2 } from './ContactGroupFormV2';
import {
  AuthorizedContactGroupContainer,
  Container,
  CreateNewGroupButtonV2,
  ProtocolContainer,
  ProtocolEducationContainer,
} from './MustBeMetProtocolForm.styles';
import { MustBeMetProtocolSkeleton } from './MustBeMetProtocolSkeleton';
import { normalizePhoneNumber } from './phoneNumber';
import { AuthorizedContactGroup, Contact, MustBeMetProtocol, newMustBeMetProtocolFormType } from './types';
import { useMustBeMetProtocol } from './useMustBeMetProtocol';

interface MustBeMetProtocolFormV2Props {
  contacts: Contact[];
  contactsLoaded?: boolean;
  onLoadingChange?: (loading: boolean) => void;
  isOnMasterSchoolList?: boolean;
  defaultIndividualGroup?: AuthorizedContactGroup | null;
  accountLocationId?: string;
  placeId?: string;
  defaultValue?: RiderLocation | MustBeMetProtocol;
}

const MEET_AT_CAR_LABEL = 'Meet at car';

const buildDefaultGroup = (contacts: Contact[], isOnMasterSchoolList: boolean): AuthorizedContactGroup => {
  const contactsWithPhoneNumber = contacts.filter((contact) => !!contact.mobilePhoneNumber);

  if (!isOnMasterSchoolList) {
    return {
      name: 'Trusted contacts',
      escalationPhoneNumber: contactsWithPhoneNumber.length
        ? normalizePhoneNumber(contactsWithPhoneNumber[0].mobilePhoneNumber)
        : '',
      verifyIdBadge: false,
      verifyRiderName: true,
      verifyRiderBirthdate: true,
    };
  }

  return {
    name: '',
    escalationPhoneNumber: '',
    verifyIdBadge: false,
    verifyRiderName: false,
    verifyRiderBirthdate: false,
  };
};

export const MustBeMetProtocolFormV2: FC<MustBeMetProtocolFormV2Props> = ({
  contacts,
  contactsLoaded = true,
  onLoadingChange,
  isOnMasterSchoolList = false,
  defaultIndividualGroup = null,
  accountLocationId,
  placeId,
  defaultValue,
}) => {
  const {
    protocolOptions,
    protocolsLoading,
    protocolsError,
    fetchMustBeMetProtocols,
    getProtocolLabelByValue,
    resetProtocolOptions,
  } = useMustBeMetProtocol();
  const {
    control,
    setValue,
    formState: { errors, submitCount },
  } = useFormContext<newMustBeMetProtocolFormType>();

  const [groupsInitialized, setGroupsInitialized] = useState(false);

  const watchAuthorizedContactGroups = useWatch({
    control,
    name: 'mustBeMetProtocol.authorizedContactGroups',
    defaultValue: defaultValue?.authorizedContactGroups || [],
  });
  const watchLocationProtocol = useWatch({
    control,
    name: 'mustBeMetProtocol.locationProtocol',
  });

  const remove = (indexToRemove: number) => {
    setValue(
      'mustBeMetProtocol.authorizedContactGroups',
      watchAuthorizedContactGroups.filter((_, index) => index !== indexToRemove),
      { shouldDirty: true }
    );
  };

  const handleAddGroup = () => {
    setValue(
      'mustBeMetProtocol.authorizedContactGroups',
      [...watchAuthorizedContactGroups, buildDefaultGroup(contacts, isOnMasterSchoolList)],
      { shouldDirty: true }
    );
  };

  // Prefers the server-provided default group over the "Trusted contacts" heuristic.
  useEffect(() => {
    if (contactsLoaded && watchAuthorizedContactGroups.length === 0) {
      if (contacts.length > 0 && defaultIndividualGroup) {
        setValue('mustBeMetProtocol.authorizedContactGroups', [defaultIndividualGroup], { shouldDirty: true });
      } else {
        handleAddGroup();
      }
    }
    if (contactsLoaded) {
      setGroupsInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when contacts finish loading
  }, [contactsLoaded]);

  useEffect(() => {
    if (accountLocationId) {
      fetchMustBeMetProtocols({ accountLocationId });
    } else if (placeId) {
      fetchMustBeMetProtocols({ placeId });
    } else {
      resetProtocolOptions();
    }
  }, [accountLocationId, placeId]);

  // Defaults the drop-off procedure to "Meet at car" once protocol options load.
  useEffect(() => {
    if (!watchLocationProtocol && protocolOptions.length > 0) {
      const meetAtCar = protocolOptions.find(
        (protocol) => protocol.label.toLowerCase() === MEET_AT_CAR_LABEL.toLowerCase()
      );

      if (meetAtCar) {
        setValue('mustBeMetProtocol.locationProtocol', meetAtCar.value, { shouldDirty: false });
      }
    }
  }, [protocolOptions, watchLocationProtocol]);

  const isDefaultProtocolPending =
    !watchLocationProtocol &&
    protocolOptions.some((protocol) => protocol.label.toLowerCase() === MEET_AT_CAR_LABEL.toLowerCase());
  const isLoading = !contactsLoaded || !!protocolsLoading || !groupsInitialized || isDefaultProtocolPending;

  useEffect(() => {
    onLoadingChange?.(isLoading || !!protocolsError);
  }, [isLoading, protocolsError, onLoadingChange]);

  const protocolInstructions = protocolOptions.find(
    (protocol) => protocol.value === watchLocationProtocol
  )?.instructions;

  if (protocolsError) {
    return (
      <Container>
        <Alert severity="error" message="Unable to load drop-off procedures. Please try again before saving." />
        <Button
          color="tertiary"
          onClick={() => fetchMustBeMetProtocols(accountLocationId ? { accountLocationId } : { placeId })}
        >
          Try again
        </Button>
      </Container>
    );
  }

  if (isLoading) {
    return <MustBeMetProtocolSkeleton groupCount={watchAuthorizedContactGroups.length} />;
  }

  return (
    <Container>
      <ProtocolContainer>
        <Box>
          <InputLabel fieldtype="required" htmlFor="location-protocol">
            Drop-off procedure
          </InputLabel>
          <Controller
            name="mustBeMetProtocol.locationProtocol"
            control={control}
            defaultValue={
              typeof defaultValue?.locationProtocol === 'object'
                ? defaultValue.locationProtocol.value
                : defaultValue?.locationProtocol
            }
            render={({ field: { value, onChange } }) => (
              <Autocomplete
                className={!!errors.mustBeMetProtocol?.locationProtocol ? 'hasError' : ''}
                value={value}
                id={'location-protocol'}
                data-testid={'location-protocol'}
                options={protocolOptions}
                autoHighlight
                getOptionLabel={(option) =>
                  typeof option === 'string' ? getProtocolLabelByValue(option) : option.label || ''
                }
                isOptionEqualToValue={(option, value) => protocolOptions.length === 0 || option.value === value.value}
                renderInput={(params) => (
                  <FormInputText
                    {...params}
                    variant="outlined"
                    error={!!errors.mustBeMetProtocol?.locationProtocol}
                    helperText={errors.mustBeMetProtocol?.locationProtocol?.message}
                  />
                )}
                onChange={(_, value) => {
                  if (typeof value === 'string') {
                    return;
                  }

                  onChange(value ? value.value : null);
                }}
              />
            )}
          />
        </Box>
        <Box>
          {protocolInstructions && <ProtocolEducationContainer>{protocolInstructions}</ProtocolEducationContainer>}
        </Box>
      </ProtocolContainer>
      <AuthorizedContactGroupContainer>
        <PageHeading variant="h2">{'Authorized individual groups'}</PageHeading>
        <Text variant="bodyM" fontWeight={300}>
          Designate groups who are authorized to meet the rider at this location.
        </Text>
        {submitCount > 0 && watchAuthorizedContactGroups.length === 0 && (
          <Alert
            severity="error"
            message="You need to create at least one group or disable this feature before saving."
          />
        )}
        {watchAuthorizedContactGroups.map((_, index) => (
          <ContactGroupFormV2
            key={index}
            index={index}
            contacts={contacts}
            control={control}
            errors={errors}
            onDelete={remove}
          />
        ))}
        {watchAuthorizedContactGroups.length < 5 && (
          <CreateNewGroupButtonV2 color="tertiary" onClick={handleAddGroup} variant="text">
            + Add authorized group
          </CreateNewGroupButtonV2>
        )}
        {watchAuthorizedContactGroups.length === 5 && (
          <Alert
            severity="warning"
            variant="outlined"
            message="You have reached the limit of 5 authorized individual groups for this rider. Consider editing or replacing an existing group. "
          />
        )}
      </AuthorizedContactGroupContainer>
    </Container>
  );
};
